import { Field, FormikValues, useFormikContext } from "formik";
import { Grid, InputAdornment, MenuItem, Typography } from "@mui/material";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { StyledTextField } from "./styled-components/AddressFields";
import { useEffect } from "react";
import { batch } from "react-redux";
import { setBirthdate } from "src/store/features/onboarding.slice";
import { useAppDispatch } from "src/store/store";
import { isValidNumber } from "./utils/useApplicationFormConfig";

export const BirthDateFields = () => {
  const formik = useFormikContext<FormikValues>();
  const {
    values,
    values: { birthDay, birthMonth, birthYear },
    errors,
  } = formik;

  type Variant = "birthDay" | "birthMonth" | "birthYear";
  const variants: Array<Variant> = ["birthDay", "birthMonth", "birthYear"];

  const isLeapYear = (year: number) => {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  };

  const getDaysInMonth = (month: string, year: number) => {
    switch (month) {
      case "02":
        return isLeapYear(year) ? 29 : 28;
      case "04":
      case "06":
      case "09":
      case "11":
        return 30;
      default:
        return 31;
    }
  };

  const allMonths = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
  ];

  const dispatch = useAppDispatch();

  useEffect(() => {
    batch(() => {
      dispatch(
        setBirthdate({
          day: birthDay,
          month: birthMonth,
          year: birthYear,
        }),
      );
    });
  }, [birthDay, birthMonth, birthYear]);

  // The user must be 18 or older today. The year list stops at
  // currentYear - 18, but someone born in that cutoff year is only 18 once
  // their birth month/day has passed — cap the month and day lists too.
  const now = new Date();
  const currentYear = now.getFullYear();
  const isCutoffYear = Number(birthYear) === currentYear - 18;
  const maxMonth = isCutoffYear ? now.getMonth() + 1 : 12;
  const isCutoffMonth = isCutoffYear && Number(birthMonth) === maxMonth;

  const months = allMonths.filter((month) => Number(month.value) <= maxMonth);

  const daysInMonth = getDaysInMonth(birthMonth, parseInt(birthYear));
  const numberOfDays = isCutoffMonth ? Math.min(daysInMonth, now.getDate()) : daysInMonth;
  const days = Array.from({ length: numberOfDays }, (_, i) => ({
    label: String(i + 1).padStart(2, "0"),
    value: String(i + 1).padStart(2, "0"),
  }));

  // Selecting the cutoff year (or month) can invalidate an earlier month/day
  // pick — clear anything that now falls outside the allowed range.
  useEffect(() => {
    if (birthMonth && Number(birthMonth) > maxMonth) {
      formik.setFieldValue("birthMonth", "");
      formik.setFieldValue("birthDay", "");
    } else if (birthDay && Number(birthDay) > numberOfDays) {
      formik.setFieldValue("birthDay", "");
    }
  }, [birthDay, birthMonth, maxMonth, numberOfDays]);

  const years = Array.from({ length: 100 }, (_, i) => {
    const yearValue = currentYear - i - 18; // Subtract 18 from the current year
    return {
      label: String(yearValue),
      value: String(yearValue),
    };
  }).filter((year) => +year.value <= currentYear - 18); // Filter out any years less than 18 years ago
  const DateFields = (props: { variant: Variant }) => {
    const { variant } = props;
    const items = variant === "birthDay" ? days : variant === "birthMonth" ? months : years;

    const ArrowDown = () => {
      return (
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_180_798)">
            <path
              d="M1.25 3.62L6.5 9.5L11.75 3.62"
              stroke="#002035"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_180_798">
              <rect width="12" height="12" fill="white" transform="translate(0.5 0.5)" />
            </clipPath>
          </defs>
        </svg>
      );
    };

    const labels = ["Day", "Month", "Year"];
    const valueSelected = !!values[variant];
    const errorMessage = errors[variant];

    return (
      <Field
        as={StyledTextField}
        select
        error={Boolean(errors[variant]) && !isValidNumber(values[variant])}
        helperText={errorMessage}
        selected={valueSelected}
        variant={"outlined"}
        name={variant}
        value={values[variant] ?? ``}
        label={labels[variants.indexOf(variant)]}
        data-testid={`${variant}-test-id`}
        SelectProps={{
          IconComponent: () => (
            <div style={{ display: valueSelected ? "none" : "flex" }}>
              <ArrowDown />
            </div>
          ),
          MenuProps: {
            PaperProps: {
              style: {
                maxHeight: "450px",
              },
            },
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <div style={{ display: valueSelected ? "flex" : "none" }}>
                <GridCheckCircleIcon sx={{ fill: "#002035", fontSize: "16px" }} />
              </div>
            </InputAdornment>
          ),
        }}>
        {items.map((item, it) => (
          <MenuItem key={item.value + it} value={item.value}>
            <Typography sx={{ textTransform: "capitalize" }}>{item.label}</Typography>
          </MenuItem>
        ))}
        <MenuItem sx={{ display: "none" }} value={""}></MenuItem>
      </Field>
    );
  };

  return (
    <Grid container spacing={2}>
      {variants.map((variant, it) => (
        <Grid item xs={4} key={variant + it}>
          <DateFields variant={variant} />
        </Grid>
      ))}
    </Grid>
  );
};
